import React from 'react';

const AIIntelligenceSection: React.FC = () => {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-28">
      {/* Subtle decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full mix-blend-multiply filter blur-3xl opacity-40"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>

      <div className="container relative z-10 mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content Side */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-blue-50 border border-blue-200">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              <span className="text-sm font-semibold text-blue-700">24/7 AI Intelligence</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 leading-tight">
              Your AI Consultant Never Sleeps
            </h2>

            <p className="text-lg font-bold text-blue-600 mb-6">
              It's like hiring a full-time business strategist, analyst, and marketer — for £19 a month.
            </p>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              24/7 monitoring of your business metrics, customer data, and market trends. It finds profit leaks while you sleep and spots missed opportunities before your competitors do. Our AI remembers every conversation, every insight, and every decision—building a deep understanding of your business that gets smarter over time.
            </p>

            {/* Key Features */}
            <div className="space-y-5">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">24/7 Business Monitoring</h3>
                  <p className="text-gray-600">Continuous analysis of sales, expenses, customer behavior, and market opportunities while you sleep.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Long-Term Memory</h3>
                  <p className="text-gray-600">Remembers every conversation, decision, and outcome. Your AI consultant learns your preferences, goals, and business context over time.</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Proactive Insights</h3>
                  <p className="text-gray-600">Identifies cost-cutting opportunities, revenue growth strategies, and potential problems before they impact your bottom line.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Side */}
          <div className="relative">
            {/* Clean card with metrics */}
            <div className="relative rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-lg">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">AI</span>
                  </div>
                  <div>
                    <div className="text-gray-900 font-semibold">Business Intelligence</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      Active • Monitoring
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500">Last 7 days</div>
              </div>

              {/* Metrics */}
              <div className="space-y-4">
                <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-700">Cost Savings Identified</span>
                    <span className="text-2xl font-bold text-gray-900">£2,340</span>
                  </div>
                  <div className="text-xs text-green-700">↑ 18% from last week</div>
                </div>

                <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-700">Revenue Opportunities</span>
                    <span className="text-2xl font-bold text-gray-900">£8,750</span>
                  </div>
                  <div className="text-xs text-blue-700">↑ 24% from last week</div>
                </div>

                <div className="rounded-xl bg-cyan-50 border border-cyan-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-700">Insights Generated</span>
                    <span className="text-2xl font-bold text-gray-900">127</span>
                  </div>
                  <div className="text-xs text-cyan-700">Based on your data</div>
                </div>
              </div>

              {/* Recent Insights */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-900 mb-3">Recent Insights</div>
                <div className="space-y-2 text-xs text-gray-600">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5"></div>
                    <span>Customer acquisition cost decreased by 12% this week</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5"></div>
                    <span>3 high-value leads went cold—recommend follow-up</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5"></div>
                    <span>Expense pattern suggests 15% cost reduction opportunity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats bar */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
              24/7
            </div>
            <div className="text-gray-600">Continuous Monitoring</div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
              100%
            </div>
            <div className="text-gray-600">Conversation Memory</div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">
              Unlimited
            </div>
            <div className="text-gray-600">Insights & Analysis</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIIntelligenceSection;
